<script setup lang="ts">
import type { ContextMenuAction } from './types';
import { mdiDotsVertical } from '@mdi/js';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { computed, ref, watch, onBeforeUnmount } from 'vue';

export interface ContextMenuProps {
  actions: ContextMenuAction[];
  disabled?: boolean;
  persistent?: boolean;
  closeOnScrollElements?: HTMLElement | HTMLElement[] | null;
}

function applyCallback(action: ContextMenuAction, event: MouseEvent | KeyboardEvent) {
  if (action.disabled) {
    event.stopImmediatePropagation();
  } else {
    action.callback?.(event);
  }
}

const props = defineProps<ContextMenuProps>();

const allActionsHidden = computed(() => props.actions.every((action) => action.hidden));

const isMenuOpen = ref(false);

const handleScroll = () => {
  isMenuOpen.value = false;
};

// Helper to normalize closeOnScrollElements to array
const normalizeToArray = (value: HTMLElement | HTMLElement[] | null | undefined): HTMLElement[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

// Track currently attached listeners for cleanup
const activeTargets = ref<HTMLElement[]>([]);

const cleanupListeners = () => {
  activeTargets.value.forEach((target) => {
    target.removeEventListener('scroll', handleScroll);
  });
  activeTargets.value = [];
};

const attachListeners = (targets: HTMLElement[]) => {
  targets.forEach((target) => {
    target.addEventListener('scroll', handleScroll, { passive: true });
  });
  activeTargets.value = targets;
};

watch(
  () => [isMenuOpen.value, props.closeOnScrollElements, props.persistent] as const,
  ([isOpen, targetRef, persistent]) => {
    cleanupListeners();

    if (!persistent && isOpen) {
      const targets = normalizeToArray(targetRef);
      attachListeners(targets);
    }
  },
);

onBeforeUnmount(() => {
  cleanupListeners();
});

const scrollStrategy = computed(() => {
  if (props.persistent) return 'reposition';

  const targetRef = props.closeOnScrollElements;
  const hasTargets = Array.isArray(targetRef)
    ? targetRef.length > 0
    : targetRef !== null && targetRef !== undefined;

  // Use 'none' when we have scroll targets (we handle it manually)
  // Use 'close' when we don't have scroll targets (global behavior)
  return hasTargets ? 'none' : 'close';
});
</script>

<template>
  <v-menu
    v-if="!allActionsHidden"
    v-model="isMenuOpen"
    class="contextMenu"
    :disabled="disabled"
    :scrollStrategy="scrollStrategy"
    contentClass="contextMenu"
  >
    <template #activator="{ props: activatorProps }">
      <TextVBtn
        class="contextMenuButton"
        color="secondary"
        v-bind="activatorProps"
        size="small"
        variant="text"
        :icon="mdiDotsVertical"
      />
    </template>
    <v-list class="elevation-4">
      <template v-for="action in actions" :key="action.text">
        <div v-if="!action.hidden">
          <v-divider v-if="action.divider"></v-divider>
          <Tooltip position="right" :disabled="!action.tooltip">
            <template #target="{ props: targetProps }">
              <v-list-item
                v-bind="targetProps"
                color="--v-theme-secondary-darken-2"
                :to="action.disabled ? undefined : action.to"
                :title="action.text"
                :disabled="action.disabled"
                :prependIcon="action.icon"
                @click="applyCallback(action, $event)"
              />
            </template>
            <template #tooltip>{{ action.tooltip }}</template>
          </Tooltip>
        </div>
      </template>
    </v-list>
  </v-menu>
</template>

<style lang="scss" scoped>
@use '@/styles/mixins';
@use '@/styles/variables';

.contextMenu {
  &:deep(.v-list) {
    padding-top: 6px;
    padding-bottom: 6px;

    .v-divider {
      margin: 4px 8px;
    }

    .v-list-item {
      margin: 2px 8px;
      padding: 0 16px;

      &-title,
      &__prepend {
        @include mixins.typography('body-2');
        font-weight: variables.$fontweight_medium;
        color: rgb(var(--v-theme-on-surface));
        line-height: 16px;
        opacity: unset;
      }

      &--density-default {
        min-height: 40px;
      }

      &__spacer {
        width: 8px;
      }

      &__overlay {
        visibility: hidden;
      }

      &:not(.v-list-item--disabled) {
        .v-icon {
          opacity: unset;
        }

        &:hover {
          color: rgb(var(--v-theme-off-white));
          background-color: rgb(var(--v-theme-secondary-darken-2));
          border-radius: 4px;
          box-shadow: inset 0px 2px 5px 0px rgb(var(--v-theme-secondary-darken-4));
          opacity: unset;

          .v-list-item-title {
            color: rgb(var(--v-theme-off-white));
          }

          .v-icon {
            color: rgb(var(--v-theme-off-white));
            opacity: unset;
          }
        }
      }

      &--disabled {
        pointer-events: unset;

        &:hover {
          background-color: rgb(var(--v-theme-secondary-lighten-5));
          border-radius: 4px;
        }
      }
    }
  }
}
</style>
